import { useEffect, useMemo, useState, type FormEvent } from 'react';
import type { PurchaseItem } from '../types';
import { isValidPurchaseQuantity, requiresIntegerQuantity } from '../lib/purchase-quantity';
import { EditIcon } from './Icons';
import { Modal } from './Modal';

interface Props {
  item: PurchaseItem | null;
  onClose: () => void;
  onApply: (key: string, quantity: string) => void;
  onReset?: (key: string) => void;
}

export function QuantityAdjustModal({ item, onClose, onApply, onReset }: Props) {
  const [draft, setDraft] = useState('');

  useEffect(() => {
    if (item) setDraft(item.finalQuantity);
  }, [item]);

  const integerOnly = item ? requiresIntegerQuantity(item.unit) : false;
  const isValid = useMemo(() => isValidPurchaseQuantity(draft, item?.unit ?? ''), [draft, item?.unit]);
  const adjusted = Boolean(item && item.finalQuantity !== item.calculatedQuantity);
  const isDaily = Boolean(item?.sources?.some((source) => source.kind !== 'extra'));
  const scopeLabel = isDaily ? '当天' : '本周';

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!item || !isValid) return;
    onApply(item.key, draft.trim());
    onClose();
  };

  const reset = () => {
    if (!item || !onReset) return;
    onReset(item.key);
    onClose();
  };

  return (
    <Modal
      open={Boolean(item)}
      title="修改采购数量"
      eyebrow={isDaily ? '当天最终采购量' : '本周最终采购量'}
      onClose={onClose}
    >
      {item && (
        <form className="quantity-adjuster" onSubmit={submit}>
          <div className="quantity-adjuster__context">
            <EditIcon size={22} />
            <span>
              <strong>系统计算 {item.calculatedQuantity}{item.unit}</strong>
              <small>只修改{scopeLabel}这次采购，不会改变菜品配方。</small>
            </span>
          </div>

          <label className="quantity-adjuster__field">
            <span>{item.name}本次采购数量</span>
            <span className="quantity-adjuster__input">
              <input
                aria-label={`${item.name}最终采购数量`}
                type="number"
                min={integerOnly ? '1' : '0.001'}
                step={integerOnly ? '1' : 'any'}
                inputMode={integerOnly ? 'numeric' : 'decimal'}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
              />
              <strong>{item.unit}</strong>
            </span>
          </label>

          {!isValid && (
            <p className="quantity-adjuster__error" role="alert">
              {integerOnly ? '请输入大于 0 的整数。' : '请输入大于 0 的数量。'}
            </p>
          )}

          <div className="quantity-adjuster__actions">
            <button className="button button--ghost" type="button" onClick={onClose}>取消</button>
            <button className="button button--primary" type="submit" disabled={!isValid}>保存数量</button>
          </div>
          {adjusted && onReset && (
            <button className="quantity-adjuster__reset" type="button" onClick={reset}>
              恢复系统计算（{item.calculatedQuantity}{item.unit}）
            </button>
          )}
        </form>
      )}
    </Modal>
  );
}
